import { ref } from 'vue';

const STORAGE_KEY = 'doctorArmBackState';

/** route.name страниц-источников — ключи в data и в restoreBackContext. */
export const PAGE_NAMES = {
    MAIN: 'main',
    PATIENT_CARD: 'patient-card',
    NOTIFICATIONS: 'notifications',
    SAFETY: 'safety',
} as const;

export type PageName = typeof PAGE_NAMES[keyof typeof PAGE_NAMES] | string;

/**
 * Данные контекста страницы-источника (формат задаётся pageName / restorer).
 * Раньше хранились в data[pageName]; теперь — в BackFrame.pageData.
 */
export type PageBackDataMap = Record<string, Record<string, unknown>>;

/** Один уровень цепочки «Назад». */
export interface BackFrame {
    fromPath: string;
    fromPageName: string;
    pageData?: Record<string, unknown>;
}

export interface BackState {
    stack: BackFrame[];
}

/** Плоский формат до стека кадров — для миграции из sessionStorage. */
interface LegacyBackState {
    fromPath?: string;
    fromPageName?: string;
    data?: PageBackDataMap;
    stack?: BackFrame[];
}

/** Состояние TreatmentHistory для восстановления скролла и дерева. */
export interface TreatmentHistoryRestoreState {
    expandedKeys: string[];
    scrollTop: number;
    /** key узла, через который пользователь ушёл с таймлайна */
    lastVisitedNodeKey?: string;
}

/** Состояние списка пациентов (PatientList) для восстановления. */
export interface PatientListRestoreState {
    scrollTop: number;
    current?: number;
}

/** Поля контекста главной страницы врача (data.main). */
export interface MainPageBackData {
    selectedPatient: number | null;
    paginationCurrent: number;
    onlyStaffPatients: boolean;
    showWidget: string;
    treatmentHistory?: TreatmentHistoryRestoreState;
    patientList?: PatientListRestoreState;
}

/** Состояние вкладки «Исследования» (LabSurveys) для восстановления. */
export interface SurveysRestoreState {
    scrollTop: number;
    /** id строки таблицы, по которой был двойной клик */
    lastVisitedRowId: number | string;
    paginationCurrent?: number;
}

/** Состояние вкладки «Опросы» (Polls) для восстановления. */
export interface PollsRestoreState {
    scrollTop: number;
    /** id опроса, выбранного перед «Просмотр» */
    selectedPollId: number | string;
    paginationCurrent?: number;
}

/** Поля контекста страницы уведомлений (data.notifications). */
export interface NotificationsBackData {
    paginationCurrent: number;
    patientQuery?: string;
}

/** Поля контекста карточки пациента (data['patient-card']). */
export interface PatientCardBackData {
    patientId: number;
    activeTabKey: string;
    treatmentHistory?: TreatmentHistoryRestoreState;
    surveys?: SurveysRestoreState;
    polls?: PollsRestoreState;
}

/** key вкладки «История лечения» в PatientCard.vue */
export const PATIENT_CARD_TAB_TREATMENT_HISTORY = '2';

/** key вкладки «Исследования» в PatientCard.vue */
export const PATIENT_CARD_TAB_SURVEYS = '3';

/** key вкладки «Опросы» в PatientCard.vue */
export const PATIENT_CARD_TAB_POLLS = '4';

export interface PendingRestoreContext {
    pageName: string;
    pageData: Record<string, unknown>;
}

let cachedState: BackState | null = null;
let pendingRestoreContext: PendingRestoreContext | null = null;

/** Инкрементируется при save/clear — для реактивности showBackButton. */
export const backStateRevision = ref(0);

const bumpRevision = () => {
    backStateRevision.value += 1;
};

const normalizeBackState = (parsed: LegacyBackState): BackState | null => {
    if (Array.isArray(parsed.stack) && parsed.stack.length > 0) {
        return { stack: parsed.stack };
    }

    if (parsed.fromPath && parsed.fromPageName) {
        return {
            stack: [{
                fromPath: parsed.fromPath,
                fromPageName: parsed.fromPageName,
                pageData: parsed.data?.[parsed.fromPageName],
            }],
        };
    }

    return null;
};

const readFromStorage = (): BackState | null => {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    if (!raw) {
        return null;
    }

    try {
        return normalizeBackState(JSON.parse(raw) as LegacyBackState);
    } catch {
        return null;
    }
};

const writeToStorage = (state: BackState | null) => {
    if (!state) {
        sessionStorage.removeItem(STORAGE_KEY);
        return;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const saveBackState = (state: BackState) => {
    cachedState = state;
    writeToStorage(state);
    bumpRevision();
};

export const getBackState = (): BackState | null => {
    if (cachedState) {
        return cachedState;
    }

    cachedState = readFromStorage();
    return cachedState;
};

export const clearBackState = () => {
    cachedState = null;
    writeToStorage(null);
    bumpRevision();
};

export const hasBackStack = (state: BackState | null): boolean => (
    Boolean(state?.stack?.length)
);

/** Добавить кадр в конец стека (каждый переход — push, без перезаписи предыдущих). */
export const pushBackFrame = (frame: BackFrame) => {
    const current = getBackState();
    const stack = current?.stack ? [...current.stack] : [];
    stack.push(frame);
    saveBackState({ stack });
};

export const setPendingRestoreContext = (context: PendingRestoreContext) => {
    pendingRestoreContext = context;
};

export const consumePendingRestoreContext = (): PendingRestoreContext | null => {
    const context = pendingRestoreContext;
    pendingRestoreContext = null;
    return context;
};

export default function useBackState() {
    return {
        saveState: saveBackState,
        getState: getBackState,
        clearState: clearBackState,
    };
}
